import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../supabaseClient';
import { track } from '../utils/analytics';

function calculateAge(dob) {
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

export default function Register() {
  const { signUp } = useAuth();

  const [form, setForm]               = useState({ displayName: '', email: '', password: '', dob: '' });
  const [errors, setErrors]           = useState({});
  const [loading, setLoading]         = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [done, setDone]               = useState(false);

  function validate() {
    const e = {};
    if (!form.displayName.trim())           e.displayName = 'You need a name. Even a fake one.';
    if (form.displayName.length > 50)       e.displayName = 'Max 50 characters.';
    if (!/\S+@\S+\.\S+/.test(form.email))  e.email       = 'That doesn\'t look like an email.';
    if (form.password.length < 8)           e.password    = 'At least 8 characters.';
    if (!form.dob)                          e.dob         = 'Date of birth is required.';
    else if (calculateAge(form.dob) < 18)   e.dob         = 'You must be 18 or older to join.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/confirmed` },
      });
      if (error) throw error;
      track('sign_up', { method: 'google' });
    } catch (err) {
      setErrors({ submit: 'Google sign in failed. Please try again.' });
      setGoogleLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await signUp({
        email:       form.email,
        password:    form.password,
        displayName: form.displayName.trim(),
        dob:         form.dob,
      });
      track('sign_up', { method: 'email' });
      setDone(true);
    } catch (err) {
      setErrors({ submit: err.message || 'Something went wrong. Try again.' });
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div className="card text-center">
            <h2 style={{ color: 'var(--gold)' }}>Confirm your existence</h2>
            <p className="text-muted mt-16" style={{ lineHeight: 1.8 }}>
              We sent a link to <strong style={{ color: 'var(--text)' }}>{form.email}</strong>.
              <br />Click it, then come back and upload your photo.
            </p>
            <p className="text-muted mt-24" style={{ fontSize: '0.8rem' }}>
              Check your spam folder if nothing arrives.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div className="container">
        <div className="card">
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '2rem', marginBottom: 6 }}>
            Join{' '}
            <span style={{ color: 'var(--gold)' }}>valid</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '80%' }}> or </span>
            <span style={{ color: 'var(--purple-light)' }}>mid</span>
          </h2>
          <p className="text-muted mb-24" style={{ fontSize: '0.9rem' }}>
            Create your profile. The internet will have thoughts.
          </p>

          {/* Google sign in */}
          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            style={{
              width: '100%',
              padding: '13px 20px',
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              fontSize: '0.95rem',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginBottom: 20,
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = 'var(--purple-light)';
              e.currentTarget.style.boxShadow = 'var(--glow-purple)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--border)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <GoogleIcon />
            {googleLoading ? 'Redirecting...' : 'Continue with Google'}
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
            color: 'var(--text-muted)', fontSize: '0.8rem',
          }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            or
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="displayName">Display name</label>
              <input
                id="displayName"
                type="text"
                placeholder="How the internet will know you"
                value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                maxLength={50}
                autoComplete="username"
              />
              {errors.displayName && <p className="form-error">{errors.displayName}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="dob">Date of birth</label>
              <input
                id="dob"
                type="date"
                value={form.dob}
                onChange={e => setForm(f => ({ ...f, dob: e.target.value }))}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                  .toISOString().split('T')[0]}
                style={{ colorScheme: 'dark' }}
              />
              {errors.dob && <p className="form-error">{errors.dob}</p>}
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                You must be 18 or older. Your age is used to personalize your feed.
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="Your actual email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                autoComplete="email"
              />
              {errors.email && <p className="form-error">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                placeholder="At least 8 characters"
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
              />
              {errors.password && <p className="form-error">{errors.password}</p>}
            </div>

            {errors.submit && <p className="form-error mb-16">{errors.submit}</p>}

            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Creating your account...' : 'Create account'}
            </button>
          </form>

          <p className="text-center text-muted mt-24" style={{ fontSize: '0.85rem' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--purple-light)', textDecoration: 'none' }}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 6.293C4.672 4.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}
